import React, { useState, useEffect } from "react";
import { createUseStyles } from "react-jss";
import { ListRow } from "../..";
import { classNames } from "../../utils/classNames";
import { getCellDataId, getCellDataTestId } from "../tableUtils";
import { TableRowAction } from "./TableRowAction";
import { V2TableCell } from "./TableCell";
import { TableDrawer } from "./TableDrawer";
import { getCellLinkProps, getBoolValueFromRow, getDrawerData, tableHasAction, getAlternatingRow, rowHasAction, } from "./utilsV2Table";
import { rowValue } from "./sharedJSS";
import { getInlineFeedback } from "./InlineFeedback";
import { useTableContext } from "../../hooks/useTableContext";
import { TableRowActionAtStart } from "./TableRowActionAtStart";
import { observe } from "mobx";
import { useFormContext } from "../../Form";
import { Observer } from "mobx-react-lite";
import { THEME } from "../../theming/theming";
import { CheckedStates } from "../../Checkbox";
export function V2TableRow(props) {
    const { gridClassName, hasAction } = useTableContext();
    let colIndex = 0;
    const { item, drawer, rowIndex } = props;
    const rowIsDisabled = getBoolValueFromRow(item, props.disabledRows);
    const rowHasRowAction = rowHasAction(item, props);
    const cellLink = getCellLinkProps(item, props?.link, props?.onSelect, props.linkBuilder);
    const drawerData = getDrawerData(item, drawer);
    const cellDataId = getCellDataId(rowIndex, props?.dataId);
    const cellDataTestId = getCellDataTestId(rowIndex, props?.dataTestId);
    const checkboxAtStart = props.form?.type === "checkbox" && props.form?.viewState !== "edit";
    if (checkboxAtStart) {
        colIndex++;
    }
    return (React.createElement(V2TableRowComponent, { ...props, gridClassName: gridClassName },
        checkboxAtStart && (React.createElement(TableRowActionAtStart, { ...props, item: item, rowIndex: rowIndex })),
        props?.headings.map((heading, i) => {
            colIndex++;
            return (React.createElement(V2TableCell, { ...props, heading: heading, key: i, colIndex: colIndex, cellLink: !rowIsDisabled ? cellLink : undefined, dataId: cellDataId && `${cellDataId}-${colIndex}`, dataTestId: cellDataTestId && `${cellDataTestId}-${colIndex}`, inlineFeedback: getInlineFeedback(props?.feedback, props?.rowKey, item, heading) }));
        }),
        (drawerData || drawer?.component) && React.createElement(TableDrawer, { ...props }),
        hasAction && rowHasRowAction && React.createElement(TableRowAction, { ...props })));
}
export function V2TableRowComponent(props) {
    if (props.form && props.item) {
        return React.createElement(Observer, null, () => React.createElement(HighlightableTableRow, { ...props }));
    }
    return React.createElement(V2TableRowDefinition, { ...props });
}
export function HighlightableTableRow(props) {
    const { form } = props;
    const { values } = useFormContext();
    const { isBulk, onSelectAll } = useTableContext();
    const highlightedRow = (updateCollection) => {
        const highlighted = updateCollection?.filter((option) => form && form?.idIdentifier && props?.item[form.idIdentifier] === option);
        return highlighted?.length === 1;
    };
    useEffect(() => setInCollection(highlightedRow(values.get(form?.name))), [props.pagination?.offset]);
    const [inCollection, setInCollection] = useState(highlightedRow(values.get(form?.name)));
    observe(values, (changes) => {
        if (isBulk &&
            props.pagination?.totalRecords ===
                (form?.name && changes.object[form?.name]?.length)) {
            onSelectAll(CheckedStates.Checked);
        }
        setInCollection(highlightedRow(form?.name && changes.object[form?.name]));
    });
    return (React.createElement(V2TableRowDefinition, { ...props, highlight: isBulk ? !inCollection : inCollection }, props.children));
}
export function V2TableRowDefinition(props) {
    const { item, rowIndex, dataId, dataTestId, highlight } = props;
    const classes = useTableRowClasses(rowIndex === getDataLength());
    const tableHasRowAction = tableHasAction(props);
    const rowHasRowAction = rowHasAction(item, props);
    const rowIsDisabled = getBoolValueFromRow(item, props.disabledRows);
    const getRowStyling = (n) => {
        if (highlight)
            return "";
        const rowAlternating = getAlternatingRow(n);
        if (rowAlternating === "even") {
            return classes.evenRow;
        }
        else {
            return classes.oddRow;
        }
    };
    return (React.createElement(ListRow, { className: classNames({
            [classes.highlight]: !!highlight,
            [classes.clickableTableRow]: tableHasRowAction && rowHasRowAction && !rowIsDisabled,
        }, classes.row, getRowStyling(rowIndex)), role: "row", ariaRowIndex: rowIndex + 1, ariaSelected: highlight, dataRowIndex: rowIndex, gridClassName: classNames(props?.gridClassName, classes.rowValue), dataId: dataId && `${dataId}-row-${rowIndex}`, dataTestId: dataTestId && `${dataTestId}-row-${rowIndex}` }, props.children));
    function getDataLength() {
        const { form } = props;
        if (form && props?.data) {
            return props?.data?.length;
        }
        if (form) {
            const { values, name } = form;
            return values[name].length - 1;
        }
        return props?.data?.length;
    }
}
export function useTableRowClasses(lastRow) {
    const { palette, transitions } = THEME;
    const { tableTheme } = useTableContext();
    const { body } = tableTheme;
    const useStyles = createUseStyles({
        clickableTableRow: {
            "& .list__cell--text": {
                cursor: "pointer",
            },
        },
        highlight: {
            backgroundColor: palette.primary[5].color,
            borderBottom: {
                color: palette.primary[5].color,
                style: "solid",
                width: 1,
            },
        },
        row: {
            padding: "0 !important",
            minHeight: "30px !important",
            "& a.list__cell--value": {
                color: palette.gray.charcoal.color,
                "&:hover, &:focus": {
                    color: palette.gray.charcoal.color,
                    textDecoration: "none",
                },
            },
            "& .list__cell--value": {
                "&:hover": {
                    textDecoration: "none !important",
                },
            },
        },
        rowValue,
        evenRow: {
            backgroundColor: body?.evenRow?.backgroundColor,
            transition: transitions?.background,
            "&:hover": {
                transition: transitions?.background,
                backgroundColor: body?.evenRow?.backgroundHoverColor,
                "& .table-listRowAction": {
                    transition: transitions?.background,
                    backgroundColor: body?.evenRow?.backgroundHoverColor,
                },
            },
            "& .table-listRowAction": {
                transition: transitions?.background,
                backgroundColor: body?.evenRow?.backgroundColor,
            },
            borderBottom: !lastRow ? body?.evenRow?.borderBottom : {},
        },
        oddRow: {
            backgroundColor: body?.oddRow?.backgroundColor,
            transition: transitions?.background,
            "&:hover": {
                transition: transitions?.background,
                backgroundColor: body?.oddRow?.backgroundHoverColor,
                "& .table-listRowAction": {
                    transition: transitions?.background,
                    backgroundColor: body?.oddRow?.backgroundHoverColor,
                },
            },
            "& .table-listRowAction": {
                transition: transitions?.background,
                backgroundColor: body?.oddRow?.backgroundColor,
            },
            borderBottom: !lastRow ? body?.oddRow?.borderBottom : {},
        },
    });
    return useStyles();
}
